import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  getListaRelease,
  getReleaseById,
  deleteRelease, 
  deleteReleaseCargo,
  postRelease,
} from '../../services/releaseService';
import {
  downloadContent
} from '../../services/contentService';
import { getErrorMessage } from '../../locales/translationUtils';
import LoadingOverlay from '../../components/Loading';
import AlertOverlay from '../../components/Alert';
import { Content, Release, ReleaseResponse } from '../../pages/Release/types';
import { ApiResponse } from '../../services/api';

interface ReleaseContextProps {
  releases: ReleaseResponse[] | null;
  release: Release | null;
  loading: boolean;
  error: string | null;
  fetchReleases: (filters: any, released?: boolean) => void;
  fetchReleaseById: (id: number) => void;
  cadastraRelease: (payload: FormData) => void;
  excluiRelease: (id: number) => Promise<ApiResponse<ReleaseResponse>>;
  excluiReleaseCargo: (id: number, cargoId: number) => Promise<ApiResponse<ReleaseResponse>>;
  setAlert: React.Dispatch<React.SetStateAction<Alert | undefined>>;
  downloadReleaseContent :(content: Content) => void;
  apiResponse: ApiResponse<ReleaseResponse> | null;
}

type Alert = {
  message: string;
  variant: 'success' | 'danger' | 'warning' | 'info';
};

interface ReleaseProviderProps {
  children: React.ReactNode;
}

const ReleaseContext = createContext<ReleaseContextProps | null>(null);

const ReleaseProvider: React.FC<ReleaseProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<Alert | undefined>(undefined);
  const [releases, setReleases] = useState<ReleaseResponse[] | null>(null);
  const [release, setRelease] = useState<Release | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<ApiResponse<ReleaseResponse> | null>(null);

  const fetchReleases = useCallback(async (filters: any, released?: boolean) => {
    setError(null);

    try {
      setLoading(true);
      const response = await getListaRelease(filters, released);
      const items: ReleaseResponse[] = response.data.values;

      setApiResponse(response);
      setReleases(items || []);
    } catch (err) {
      setReleases([]);
      setError(getErrorMessage('fetchReleaseError'));
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchReleaseById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getReleaseById(id);
      
      setRelease(response.data);
    } catch (err) {
      setError(getErrorMessage("fetchReleaseByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  
  const excluiRelease = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      return await deleteRelease(id);
    } catch (err: any) {
      setError(getErrorMessage("deleteReleaseError"));
      return err?.response?.data as ApiResponse<ReleaseResponse>;
    } finally {
      setLoading(false);
    }
  }, []);
  
  const excluiReleaseCargo = useCallback(async (id: number, cargoId: number) => {
    setError(null);
    try {
      setLoading(true);
      return await deleteReleaseCargo(id, cargoId);
    } catch (err: any) {
      setError(getErrorMessage("deleteReleaseError"));
      return err?.response?.data as ApiResponse<ReleaseResponse>;
    } finally {
      setLoading(false);
    }
  }, []);

  const cadastraRelease = useCallback(
    async (payload: FormData, filters?: any) => {
      setError(null);
  
      try {
        setLoading(true);
        const result = await postRelease(payload);
  
        if (filters) {
          await fetchReleases(filters);
        }
  
        // Usa mensagem do backend se disponível, senão usa mensagem padrão
        const message = result.messages?.length > 0 
          ? result.messages[0] 
          : 'Cargo liberado com sucesso!';
          
        setAlert({
          message: message,
          variant: 'success',
        });
      } catch (error) {
        if (error instanceof Error) {
          setAlert({
            message: error.message,
            variant: 'danger',
          });
        } else {
          setError(getErrorMessage('registerReleaseError'));
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchReleases]
  );

  const downloadReleaseContent = useCallback(async (content: Content) => {
    try {
      const response = await downloadContent(content.id);
        
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${content.description}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(getErrorMessage("downloadReleaseContentError", {}));
      throw error;
    }
  }, []);
  
  return (
    <ReleaseContext.Provider
      value={{
        releases,
        release,
        loading,
        error,
        fetchReleases,
        fetchReleaseById,
        excluiRelease,
        excluiReleaseCargo,
        cadastraRelease,
        setAlert,
        downloadReleaseContent,
        apiResponse,
      }}
    >
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(undefined)}
        />
      )}

      <LoadingOverlay isLoading={loading} />
      {children}
    </ReleaseContext.Provider>
  );
};

export default ReleaseProvider;

export const useRelease = () => {
  const context = useContext(ReleaseContext);
  if (!context) {
    throw new Error(
      getErrorMessage('wrongUseProviderError', {
        auth: useRelease?.name,
        providerName: ReleaseProvider?.name,
      })
    );
  }
  return context;
};
